import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, HelpCircle, ChevronDown, ChevronUp, 
  ShoppingCart, Truck, Beaker, CreditCard, Shield, Package,
  Clock, FileText, AlertTriangle, Mail
} from 'lucide-react';

const FAQPage = () => {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (id) => {
    setOpenItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const faqCategories = [
    {
      id: 'ordering',
      title: 'Ordering & Account',
      icon: ShoppingCart,
      color: 'purple',
      questions: [
        {
          q: 'Do I need an account to place an order?',
          a: 'Yes, we require all customers to create an account before purchasing. This helps us verify that our products are being used for legitimate research purposes and allows you to track your orders, view order history, and manage your shipping addresses.'
        },
        {
          q: 'What payment methods do you accept?',
          a: 'We accept major credit cards (Visa, MasterCard, American Express, Discover), as well as CashApp and Venmo for your convenience. All transactions are processed securely through our encrypted payment system.'
        },
        {
          q: 'Can I modify or cancel my order after placing it?',
          a: 'Orders can be modified or cancelled within 1 hour of placement. After this window, orders enter processing and cannot be changed. Please contact us immediately at support@amino-chain.com if you need to make changes.'
        },
        {
          q: 'Do you offer wholesale pricing?',
          a: 'Yes! We offer wholesale pricing for qualified research institutions, laboratories, and businesses. Wholesale customers receive tiered discounts based on order volume. Contact us to apply for a wholesale account.'
        },
        {
          q: 'Is there a minimum order amount?',
          a: 'No, there is no minimum order amount. However, orders over $200 qualify for free standard shipping within the United States.'
        }
      ]
    },
    {
      id: 'shipping',
      title: 'Shipping & Delivery',
      icon: Truck,
      color: 'blue',
      questions: [
        {
          q: 'How long does shipping take?',
          a: 'Standard shipping takes 5-7 business days, expedited shipping takes 2-3 business days, and overnight shipping delivers within 1 business day. Orders are typically processed and shipped within 1-2 business days.'
        },
        {
          q: 'Do you ship internationally?',
          a: 'Currently, we only ship within the United States (all 50 states). International shipping is not available at this time due to varying regulations on research compounds in different countries.'
        },
        {
          q: 'How are the peptides packaged for shipping?',
          a: 'All peptides are shipped in temperature-appropriate packaging to maintain product integrity. During warmer months, we include cold packs at no additional charge. Products are shipped in discreet, unmarked packaging for your privacy.'
        },
        {
          q: 'Can I ship to a P.O. Box?',
          a: 'Standard shipping can be delivered to P.O. Boxes. However, expedited and overnight shipping methods require a physical street address and cannot be delivered to P.O. Boxes.'
        },
        {
          q: 'How can I track my order?',
          a: 'Once your order ships, you will receive an email with tracking information. You can also log into your account to view tracking details for all your orders.'
        }
      ]
    },
    {
      id: 'products',
      title: 'Products & Research Use',
      icon: Beaker,
      color: 'green',
      questions: [
        {
          q: 'What are your products intended for?',
          a: 'All AminoChain products are sold exclusively for in-vitro laboratory research and educational purposes. They are NOT intended for human or animal consumption, therapeutic use, or diagnostic procedures. By purchasing, you agree to use products only for legitimate research purposes.'
        },
        {
          q: 'What purity level are your peptides?',
          a: 'All our peptides are guaranteed to be 99%+ purity, verified through HPLC (High-Performance Liquid Chromatography) analysis. Every batch comes with a Certificate of Analysis (COA) documenting the exact purity and specifications.'
        },
        {
          q: 'Do you provide Certificates of Analysis (COA)?',
          a: 'Yes, every product includes a detailed Certificate of Analysis. COAs are available for download on each product page and are also included with your shipment. They document purity, molecular weight, and other relevant specifications.'
        },
        {
          q: 'How should I store the peptides?',
          a: 'Most peptides should be stored at -20°C for long-term storage or 2-8°C for short-term storage. Specific storage recommendations are included with each product. Keep peptides away from light and moisture, and store in the original sealed vials until use.'
        },
        {
          q: 'What is the shelf life of your peptides?',
          a: 'When stored properly (typically -20°C), our lyophilized peptides have a shelf life of 2-3 years. Reconstituted peptides should be used within 2-4 weeks when refrigerated. Always check the specific product documentation for exact shelf life information.'
        }
      ]
    },
    {
      id: 'returns',
      title: 'Returns & Refunds',
      icon: Package,
      color: 'amber',
      questions: [
        {
          q: 'What is your return policy?',
          a: 'We accept returns within 14 days of delivery for unopened, unused products in their original sealed packaging. Due to the nature of research compounds, we cannot accept returns on opened products. A Return Merchandise Authorization (RMA) number is required for all returns.'
        },
        {
          q: 'What if I receive a damaged product?',
          a: 'If you receive a damaged or defective product, please contact us within 48 hours of delivery with photos of the damage. We will ship a replacement at no charge or issue a full refund. Do not discard the product or packaging until instructed.'
        },
        {
          q: 'How long does it take to process a refund?',
          a: 'Refunds are processed within 5-7 business days of receiving the returned product. Please allow an additional 5-10 business days for the refund to appear on your statement, depending on your financial institution.'
        },
        {
          q: 'Do you charge restocking fees?',
          a: 'No, we do not charge restocking fees for eligible returns. However, the customer is responsible for return shipping costs unless the return is due to our error (wrong item shipped, defective product, etc.).'
        }
      ]
    },
    {
      id: 'compliance',
      title: 'Compliance & Legal',
      icon: Shield,
      color: 'red',
      questions: [
        {
          q: 'Are your products legal to purchase?',
          a: 'Yes, our research peptides are legal to purchase in the United States for legitimate research purposes. They are not controlled substances and do not require special licensing for research use. However, they are strictly prohibited for human consumption.'
        },
        {
          q: 'Do I need any special permits or licenses?',
          a: 'For standard research use, no special permits or licenses are required. However, if you are conducting research that falls under specific regulatory frameworks (FDA, DEA, etc.), you may need appropriate approvals from those agencies.'
        },
        {
          q: 'Why do you require age verification?',
          a: 'We require all customers to be 21 years or older to ensure our products are being purchased by adults capable of handling research compounds responsibly. This is part of our commitment to responsible sales practices.'
        },
        {
          q: 'Can I resell your products?',
          a: 'Reselling our products requires prior written authorization from AminoChain. Unauthorized resale may result in account termination and legal action. If you are interested in becoming an authorized reseller, please contact us.'
        }
      ]
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      purple: 'bg-purple-100 text-purple-600',
      blue: 'bg-blue-100 text-blue-600',
      green: 'bg-green-100 text-green-600',
      amber: 'bg-amber-100 text-amber-600',
      red: 'bg-red-100 text-red-600'
    };
    return colors[color] || colors.purple;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-900 to-purple-700 text-white py-16 pt-32">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/" className="inline-flex items-center text-purple-200 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Link>
          <div className="flex items-center gap-3">
            <HelpCircle className="w-10 h-10 text-amber-400" />
            <h1 className="text-4xl font-bold">Frequently Asked Questions</h1>
          </div>
          <p className="text-purple-200 mt-2">Find answers to common questions about our products and services</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        
        {/* Quick Links */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-12">
          {faqCategories.map((category) => {
            const Icon = category.icon;
            return (
              <a
                key={category.id}
                href={`#${category.id}`}
                className="flex flex-col items-center p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow text-center"
              >
                <div className={`p-3 rounded-full ${getColorClasses(category.color)} mb-2`}>
                  <Icon className="w-5 h-5" />
                </div>
                <span className="text-sm font-medium text-gray-700">{category.title}</span>
              </a>
            );
          })}
        </div>

        {/* FAQ Sections */}
        <div className="space-y-8">
          {faqCategories.map((category) => {
            const Icon = category.icon;
            return (
              <div key={category.id} id={category.id} className="scroll-mt-32">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`p-2 rounded-lg ${getColorClasses(category.color)}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">{category.title}</h2>
                </div>
                
                <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                  {category.questions.map((item, idx) => {
                    const itemId = `${category.id}-${idx}`;
                    const isOpen = openItems[itemId];
                    return (
                      <div key={idx} className={idx > 0 ? 'border-t border-gray-100' : ''}>
                        <button
                          onClick={() => toggleItem(itemId)}
                          className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                        >
                          <span className="font-medium text-gray-900 pr-4">{item.q}</span>
                          {isOpen ? (
                            <ChevronUp className="w-5 h-5 text-purple-600 flex-shrink-0" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                          )}
                        </button>
                        {isOpen && (
                          <div className="px-5 pb-5">
                            <p className="text-gray-600 leading-relaxed">{item.a}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Still Have Questions */}
        <div className="mt-12 bg-gradient-to-r from-purple-600 to-purple-700 rounded-2xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-2">Still Have Questions?</h3>
          <p className="text-purple-200 mb-6">Our support team is here to help you with any questions you may have.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="mailto:support@amino-chain.com"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-purple-700 font-semibold rounded-lg hover:bg-purple-50 transition-colors"
            >
              <Mail className="w-5 h-5" />
              support@amino-chain.com
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
};

export default FAQPage;
